import React, { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import { Button, Input, useToast } from '@/components/ui';
import MultiSelect from '@/components/ui/MultiSelect';
import CronScheduler from '@/components/ui/CronScheduler';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  campaignsService,
  type Campaign,
  type ScriptQuestion,
  type UpdateCampaignRequest,
} from '@/services/api/campaigns';
import { ChevronDown, FileText } from 'lucide-react';

// ── Lead categories (static list, replace later) ──────────────────────────────
const LEAD_CATEGORY_OPTIONS = [
  { value: 'medicare', label: 'Medicare' },
  { value: 'life_insurance', label: 'Life Insurance' },
  { value: 'dental', label: 'Dental' },
  { value: 'health', label: 'Health' },
  { value: 'auto', label: 'Auto Insurance' },
  { value: 'home', label: 'Home Insurance' },
  { value: 'seniors', label: 'Seniors' },
  { value: 'family', label: 'Family' },
  { value: 'business', label: 'Business' },
];

const STATUSES: Campaign['status'][] = ['active', 'inactive', 'draft', 'completed'];

const buildScriptFromText = (raw: string): ScriptQuestion[] =>
  raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((question, idx) => ({
      id: `q-${Date.now()}-${idx}`,
      question,
      type: 'text',
      required: false,
    }));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign | null;
  onSuccess: () => void;
}

const EditCampaignModal: React.FC<Props> = ({ isOpen, onClose, campaign, onSuccess }) => {
  const { success, error: toastError } = useToast();
  const [agents, setAgents] = useState<{ uuid: string; display_name: string }[]>([]);
  const [scriptText, setScriptText] = useState('');

  // Fetch available agents once
  useEffect(() => {
    campaignsService.getAvailableAgents().then((res: any) => {
      setAgents(res?.attributes?.callingAgents ?? []);
    });
  }, []);

  const agentOptions = agents.map((a) => ({
    value: a.uuid,
    label: a.display_name,
  }));

  const formik = useFormik<UpdateCampaignRequest>({
    initialValues: {
      name: campaign?.name ?? '',
      description: campaign?.description ?? '',
      status: campaign?.status ?? 'draft',
      lead_categories: campaign?.lead_categories ?? [],
      assigned_agent_ids: campaign?.assigned_agent_ids ?? [],
      scheduled_at: campaign?.scheduled_at ?? '0 9 * * *',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Campaign name is required'),
      lead_categories: Yup.array().min(1, 'Select at least one lead category'),
    }),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!campaign) return;
      try {
        await campaignsService.update(campaign.uuid, { ...values, script: buildScriptFromText(scriptText) });
        success('Campaign updated successfully');
        onSuccess();
        onClose();
      } catch (e: any) {
        toastError(e?.message || 'Failed to update campaign');
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Sync script state when campaign changes
  useEffect(() => {
    setScriptText((campaign?.script ?? []).map((q) => q.question).join('\n'));
  }, [campaign]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Campaign"
      size="lg"
      footer={
        <div className="flex justify-end gap-3 px-2">
          <Button
            type="submit"
            form="edit-campaign-form"
            disabled={formik.isSubmitting}
            className="bg-primary-button hover:bg-primary-button text-white"
          >
            {formik.isSubmitting ? 'Saving…' : 'Save Changes'}
          </Button>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <form id="edit-campaign-form" onSubmit={formik.handleSubmit} className="px-4 py-4 space-y-5">
        {/* Name */}
        <div>
          <label className="text-xs font-medium mb-1 block">Campaign Name *</label>
          <Input
            name="name"
            value={formik.values.name as string}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="e.g. Medicare Outreach Q3"
          />
          {formik.touched.name && formik.errors.name && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium mb-1 block">Description</label>
          <textarea
            name="description"
            value={(formik.values.description as string) ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows={2}
            placeholder="Brief description of this campaign…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium mb-1 block">Status</label>
          <div className="relative">
            <select
              name="status"
              value={(formik.values.status as string) ?? 'draft'}
              onChange={formik.handleChange}
              className="w-full h-10 border border-gray-300 rounded-lg px-3 py-2 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Cron Schedule */}
        <CronScheduler
          label="Recurring Schedule"
          value={((formik.values.scheduled_at as string) ?? '0 9 * * *')}
          onChange={(cron) => formik.setFieldValue('scheduled_at', cron)}
        />

        {/* Lead Categories (multi-select) */}
        <div>
          <label className="text-xs font-medium mb-1 block">Lead Categories *</label>
          <MultiSelect
            options={LEAD_CATEGORY_OPTIONS}
            value={(formik.values.lead_categories as string[]) ?? []}
            onChange={(val) => formik.setFieldValue('lead_categories', val)}
            placeholder="Select lead categories…"
          />
          {formik.touched.lead_categories && formik.errors.lead_categories && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.lead_categories as string}</p>
          )}
        </div>

        {/* Assign Agents (multi-select) */}
        <div>
          <label className="text-xs font-medium mb-1 block">Assign Agents</label>
          <MultiSelect
            options={agentOptions}
            value={(formik.values.assigned_agent_ids as string[]) ?? []}
            onChange={(val) => formik.setFieldValue('assigned_agent_ids', val)}
            placeholder="Select agents to assign…"
          />
        </div>

        {/* Script */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
            <FileText size={12} />
            Call Script (Questions)
          </label>
          <textarea
            value={scriptText}
            onChange={(e) => setScriptText(e.target.value)}
            rows={8}
            placeholder={'Enter one question per line.\nExample:\nAre you currently enrolled in Medicare?\nWould you like to learn about supplement plans?'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
          />
          <p className="text-xs text-gray-500 mt-1.5">
            One line = one question. This will be converted to campaign script questions automatically.
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default EditCampaignModal;
